'use client'

import * as React from 'react'

import dynamic from 'next/dynamic'

import type { SitePopup } from '@/lib/popups/types'

// Idle-gated client wrapper (same shape as the CaptionCall popup's lazy.tsx):
// the controller + templates only load after the page is idle, keeping them
// out of the LCP critical path. `ssr: false` is only legal in a client
// component, hence this thin wrapper.
const LazySitePopups = dynamic(() => import('./index').then((m) => ({ default: m.SitePopups })), {
  ssr: false,
  loading: () => null,
})

const hasForceShowFlag = () => {
  try {
    return new URLSearchParams(window.location.search).has('showPopup')
  } catch {
    return false
  }
}

export const SitePopups = ({ popups }: { popups: SitePopup[] }) => {
  const [enabled, setEnabled] = React.useState(false)
  const count = popups.length

  React.useEffect(() => {
    // The force flag wins even with an empty payload: admins preview DRAFT
    // popups (never in the payload) via the controller's endpoint fallback.
    if (hasForceShowFlag()) {
      setEnabled(true)
      return undefined
    }

    if (count === 0) {
      setEnabled(false)
      return undefined
    }

    const load = () => setEnabled(true)

    if (document.readyState === 'complete') {
      if (typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(load, { timeout: 5000 })
        return () => window.cancelIdleCallback(id)
      }

      const id = window.setTimeout(load, 2500)
      return () => window.clearTimeout(id)
    }

    let idleId: number | null = null
    let timerId: number | null = null

    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(load, { timeout: 5000 })
        return
      }

      timerId = window.setTimeout(load, 2500)
    }

    window.addEventListener('load', schedule, { once: true })

    return () => {
      window.removeEventListener('load', schedule)
      if (idleId !== null) window.cancelIdleCallback(idleId)
      if (timerId !== null) window.clearTimeout(timerId)
    }
  }, [count])

  if (!enabled) {
    return null
  }

  return <LazySitePopups popups={popups} />
}
